import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { IPlannedIncomeRepository } from '@app/domain/ports';
import type { PlannedIncome } from '@app/domain/entities';
import type { PlannedIncomeFilter, PlannedIncomeCursor } from '@app/domain/ports';

interface PlannedIncomeRow {
  id: number;
  planDate: number; // Unix timestamp in seconds
  accountId: number;
  accountName: string;
  categoryId: number;
  categoryName: string;
  amount: number;
  currencyCode: string;
  period: number;
  note: string;
  isActive: number;
}

export class PlannedIncomeRepository extends BaseSqliteRepository implements IPlannedIncomeRepository {
  private readonly BASE_FROM = `
    FROM planned_incomes pi
    INNER JOIN accounts a ON pi.account_id = a._id
    INNER JOIN categories cat ON pi.category_id = cat._id
    INNER JOIN currencies c ON pi.currency_id = c._id
    INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
    WHERE pi.is_deleted = 0
    AND pi.is_active = 1
  `;

  private readonly BASE_FIELDS = [
    'pi._id as id',
    'pi.plan_date as planDate',
    'pi.account_id as accountId',
    'a.name as accountName',
    'pi.category_id as categoryId',
    'cat.name as categoryName',
    'pi.amount as amount',
    'cl.ticker as currencyCode',
    'pi.period as period',
    'pi.note as note',
    'pi.is_active as isActive',
  ];

  private readonly COUNT_QUERY = `
    SELECT COUNT(*) AS total
    ${this.BASE_FROM}
  `;

  async count(filter?: PlannedIncomeFilter): Promise<number> {
    const { query, params } = this.applyFilter(this.COUNT_QUERY, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async findAll(cursor: PlannedIncomeCursor, filter?: PlannedIncomeFilter): Promise<PlannedIncome[]> {
    const sql = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
    `;

    // eslint-disable-next-line prefer-const
    let { query, params } = this.applyFilter(sql, filter);

    query += ` ORDER BY pi.plan_date ASC, pi._id ASC LIMIT ? OFFSET ?`;
    params.push(cursor.limit, cursor.offset ?? 0);

    const rows = await this.query<PlannedIncomeRow>(query, params);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async getById(id: number): Promise<PlannedIncome | null> {
    const query = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
       AND pi._id = ?`;
    const row = await this.queryOne<PlannedIncomeRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async insert(entry: Omit<PlannedIncome, 'id' | 'accountName' | 'categoryName'>): Promise<number> {
    const date = new Date(entry.planDate).setUTCHours(0, 0, 0, 0) / 1000;

    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO planned_incomes (plan_date, account_id, category_id, amount, currency_id, period, note, is_active)
       VALUES (?, ?, ?, ?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?), ?, ?, 1)
       RETURNING _id AS id`,
      [date, entry.accountId, entry.categoryId, entry.amount, entry.currencyCode, entry.period, entry.note],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO planned_incomes', [], 'Failed to insert planned income');
    }

    return result.id;
  }

  async update(entry: Omit<PlannedIncome, 'accountName' | 'categoryName'>): Promise<void> {
    const date = new Date(entry.planDate).setUTCHours(0, 0, 0, 0) / 1000;

    await this.execute(
      `UPDATE planned_incomes
       SET plan_date = ?,
           account_id = ?,
           category_id = ?,
           amount = ?,
           currency_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
           period = ?,
           note = ?,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [date, entry.accountId, entry.categoryId, entry.amount, entry.currencyCode, entry.period, entry.note, entry.id],
    );
  }

  async delete(id: number): Promise<void> {
    await this.execute("UPDATE planned_incomes SET is_deleted = 1, updated_at = strftime('%s','now') WHERE _id = ?", [
      id,
    ]);
  }

  async deactivate(id: number): Promise<void> {
    await this.execute("UPDATE planned_incomes SET is_active = 0, updated_at = strftime('%s','now') WHERE _id = ?", [
      id,
    ]);
  }

  async findDue(date: Date): Promise<PlannedIncome[]> {
    const todayMidnight = new Date(date).setUTCHours(0, 0, 0, 0) / 1000;
    const sql = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
      AND pi.plan_date <= ?
      AND c.is_hidden = 0
      ORDER BY pi.plan_date ASC, pi._id ASC
    `;
    const rows = await this.query<PlannedIncomeRow>(sql, [todayMidnight]);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async bulkUpdateCategory(sourceCategoryId: number, targetCategoryId: number): Promise<void> {
    await this.execute(
      `UPDATE planned_incomes
       SET category_id = ?,
           updated_at = strftime('%s','now')
       WHERE category_id = ? AND is_deleted = 0`,
      [targetCategoryId, sourceCategoryId],
    );
  }

  async countByAccount(accountId: number): Promise<number> {
    const result = await this.queryOne<{ count: number }>(
      `SELECT COUNT(*) as count FROM planned_incomes WHERE account_id = ? AND is_deleted = 0`,
      [accountId],
    );
    return result?.count ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE planned_incomes
       SET account_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  async countByCategory(categoryId: number): Promise<number> {
    const query = `
      SELECT COUNT(*) AS total
      FROM planned_incomes pi
      WHERE pi.category_id = ? AND pi.is_deleted = 0
    `;
    const result = await this.queryOne<{ total: number }>(query, [categoryId]);
    return result?.total ?? 0;
  }

  private applyFilter(
    query: string,
    filter?: PlannedIncomeFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.accountId) {
      query += ` AND pi.account_id = ?`;
      params.push(filter.accountId);
    }

    if (filter?.categoryId) {
      query += ` AND cat._id IN (
        SELECT ? AS cat_id
        UNION ALL
        SELECT _id FROM categories WHERE parent_category_id = ? AND is_deleted = 0
      )`;
      params.push(filter.categoryId);
      params.push(filter.categoryId);
    }

    if (filter?.currencyCode) {
      query += ` AND cl.ticker = ?`;
      params.push(filter.currencyCode);
    }

    if (filter?.dateFrom) {
      query += ` AND pi.plan_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND pi.plan_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.amountFrom) {
      query += ` AND pi.amount >= ?`;
      params.push(filter.amountFrom);
    }

    if (filter?.amountTo) {
      query += ` AND pi.amount <= ?`;
      params.push(filter.amountTo);
    }

    if (filter?.period !== undefined && filter.period !== null) {
      query += ` AND pi.period = ?`;
      params.push(filter.period);
    }

    return { query, params };
  }

  private mapRowToEntity(row: PlannedIncomeRow): PlannedIncome {
    return {
      id: row.id,
      planDate: new Date(row.planDate * 1000),
      accountId: row.accountId,
      accountName: row.accountName,
      categoryId: row.categoryId,
      categoryName: row.categoryName,
      amount: row.amount,
      currencyCode: row.currencyCode,
      period: row.period,
      note: row.note ?? '',
      isActive: row.isActive === 1,
    };
  }
}
